
import { Box, Button, Card, CardActions, CardContent, CardHeader, Collapse, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport } from "@elynx/pro-regular-svg-icons";
import { DataFileImportActionEnum, DataFileImportActionItem, DataFileImportItem } from "./DataFileImportListUtils";
import { ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import ActionMenu from "../../../components/action-menu/ActionMenu";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { DataFileImportObjectsCard } from "./DataFileImportObjectsCard";
import { DataFileImportTagsCard } from "./DataFileImportTagsCard";

export type DataFileImportListItemProps = {
    item: DataFileImportItem,
    onAction: (action: DataFileImportActionEnum, payload: DataFileImportItem) => void,
};

export function DataFileImportListItem({
    item,
    onAction,
}: DataFileImportListItemProps) {

    const [expanded, setExpanded] = useState(false);
    const [tagsExpanded, setTagsExpanded] = useState(false);
    const theme = useTheme();
    const { t } = useTranslation();

    const actionItems: DataFileImportActionItem[] = useMemo(() => {

        const actions = [{
            label: t("dataFileImport.actions.run"),
            action: DataFileImportActionEnum.Run,
            onClick: () => {
                onAction(DataFileImportActionEnum.Run, item);
            },
        },{
            label: t("dataFileImport.actions.edit"),
            action: DataFileImportActionEnum.Edit,
            onClick: () => {
                onAction(DataFileImportActionEnum.Edit, item);
            },
        },{

            label: t("dataFileImport.actions.rename"),
            action: DataFileImportActionEnum.Rename,
            onClick: () => {
                onAction(DataFileImportActionEnum.Rename, item);
            },
        },{
            label: t("dataFileImport.actions.delete"),
            action: DataFileImportActionEnum.Delete,
            onClick: () => {
                onAction(DataFileImportActionEnum.Delete, item);
            },
        }];

        return actions;
    }, [
        t,
        onAction,
        item
    ]);

    function getTitle(): ReactNode {
        return (
            <Box>
                {item.name}
            </Box>
        );
    }

    function getSubheader(): ReactNode {
        return (
            <Box>
                {t('dataFileImport.importType', {name: ImportTypeEnum[item.importType]})}
            </Box>
        );
    }

    function renderColumns() : ReactNode {
        const columns = item.columns.map(o => `${o.name}`).join(', ');
        return (
            renderProperty(t('dataFileImport.columns'), columns)
        )
    }

    function renderProperty(label: string, value: string) : ReactNode {
        return (
            <Stack
                sx={{
                    flexDirection: 'row',
                    flex: '1 1 auto'
                }}>
                <Typography
                    variant='body2'
                    sx={{
                        display: 'inline-flex',
                        fontWeight: 600,
                        flexBasis: '15%'
                    }}>
                    {label}
                </Typography>
                <Typography  variant='body2'  sx={{
                    display: 'flex'
                }}>
                    {value}
                </Typography>
            </Stack>
        );
    }


    function renderDateTimeColumns() : ReactNode {

        return (
            <>
                {renderProperty(t('dataFileImport.dateColumn'), item.dateColumn?.name) }

                {
                    item.timeColumn &&
                    renderProperty(t('dataFileImport.timeColumn'), item.timeColumn?.name) 
                }
            </>
        )
    }


    function getAvatar() {
        return (
            <Box sx={{
                position: "relative",
                width: (theme) => theme.spacing(4),
                display: 'flex'
            }}
            >
                {/* {item.type === ReportTypeEnum.GroupMarketing ?
                <i className='sw sw-money-graph fa-lg half-right' style={{ fontSize: '2rem', marginLeft: "2px" }} /> : 
                item.type === ReportTypeEnum.Custom ?
                <Box className="fa-layers fa-fw" position='relative' marginRight={theme.spacing(2)}>
                    <FontAwesomeIcon icon={faFile} size="2x" color={theme.palette.grey[800]} />
                        <FontAwesomeIcon icon={faCog} 
                            style={{
                                top: '1px',
                                left: '3px'
                            }}
                            color={theme.palette.grey[800]}
                            //color={'green'}
                            transform="shrink-1" 
                        />
                    </Box>
                 : */}

                <FontAwesomeIcon icon={faFileImport} fixedWidth={true} size="2x" />
                {/* } */}
                
            </Box>
        );
    }

    return (
        <Card variant="outlined" sx={{minHeight: '4rem'}}>
            <CardHeader
                sx={{
                    padding: 1,
                    paddingBottom: 0
                }}
                title={getTitle()}
                avatar={getAvatar()}
                subheader={getSubheader()}
                action={<ActionMenu actionItems={actionItems} />}
            />
            
            <CardContent sx={{
                marginLeft: theme.spacing(5),
                paddingBottom: 0
            }}>
                <Stack direction='column'>
                    { renderProperty(t('dataFileImport.importObjectType'), getObjectTypeName(item.importObjectType))}
                    
                    { renderColumns() }

                    { item.identifierColumn && 
                        renderProperty(t('dataFileImport.identifierColumn'), item.identifierColumn?.name)
                    }

                    { renderDateTimeColumns() }

                    {renderProperty(t('dataFileImport.timezone'), item.timezone?.standardName ?? '')}
                    
                </Stack>
            </CardContent>

            {
            (item.objectMap?.length ?? 0) > 0 && 
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>

                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => { setExpanded(!expanded)}}
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    {t('dataFileImport.objectMap', {count: item.objectMap?.length})}
                </Button>

            </CardActions>
            }


            <Collapse in={expanded} timeout="auto">
                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4)
                }}>
                    <DataFileImportObjectsCard items={item.objectMap ?? []} />
                </CardContent>
            </Collapse>


            {item.tagTypeMap.length > 0 && 
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>

                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => { setTagsExpanded(!tagsExpanded)}
                    }
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !tagsExpanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    {t('dataFileImport.tagMap', {count: item.tagTypeMap?.length})}
                </Button>
            </CardActions>
            }

            <Collapse in={tagsExpanded} timeout="auto">
                <CardContent sx={{
                    padding: '0!important',
                    margin: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                    minHeight: theme.spacing(4),
                    maxHeight: 300,
                    overflow: 'hidden',
                    overflowY: 'auto'
                }}>

                    <DataFileImportTagsCard items={item.tagTypeMap ?? []} />
                </CardContent>
            </Collapse>

        </Card>
    );
}
